import { reactive, shallowRef } from 'vue'

import { BaseFieldClass } from '@/models/fields/baseField.class'
import { isEqual } from '@/utils/isEqual'

export interface IPendingFieldChange {
  // Исходный объект поля, значение которого пока не изменяем.
  field: BaseFieldClass

  // Новое значение, ожидающее подтверждения пользователя.
  value: unknown
}

export const useFieldChangeConfirm = () => {
  // Изменение, для которого сейчас открыто окно подтверждения.
  const pendingFieldChange = shallowRef<IPendingFieldChange | null>(null)

  // Кеш прокси-объектов. WeakMap не мешает сборке удалённых из формы полей.
  const confirmableFields = new WeakMap<BaseFieldClass, BaseFieldClass>()

  // Версия редактора каждого поля. После отмены увеличиваем её, чтобы новый
  // Vue key пересоздал только этот редактор и сбросил его внутреннее состояние.
  const fieldRenderVersions = reactive(new WeakMap<BaseFieldClass, number>())

  // Во время начальной настройки компонентов значения можно нормализовать
  // без показа модального окна. Перехват включается после монтирования формы.
  let changeConfirmationIsReady = false

  // Запоминает новое значение, не записывая его в исходный объект поля.
  const requestValueChange = (field: BaseFieldClass, value: unknown) => {
    if (isEqual(field.value, value)) return

    pendingFieldChange.value = { field, value }
  }

  // Возвращает поле без изменений либо прокси, который перехватывает запись
  // в value для полей с включённым подтверждением.
  const getEditableField = (field: BaseFieldClass): BaseFieldClass => {
    if (!field.onChangeConfirm) return field

    const cachedField = confirmableFields.get(field)
    if (cachedField) return cachedField

    const confirmableField = new Proxy(field, {
      get (target, property) {
        return Reflect.get(target, property, target)
      },
      set (target, property, value) {
        if (property === 'value' && changeConfirmationIsReady) {
          requestValueChange(target, value)
          return true
        }

        return Reflect.set(target, property, value, target)
      }
    })

    confirmableFields.set(field, confirmableField)
    return confirmableField
  }

  // Возвращает Vue key редактора. Он изменяется только после отмены.
  const getEditableFieldKey = (field: BaseFieldClass): number => {
    return fieldRenderVersions.get(field) ?? 0
  }

  // Обрабатывает отдельные update-события компонентов, которые изменяют
  // значение не через сеттер прокси. true означает, что изменение отложено.
  const requestFieldUpdate = (field: BaseFieldClass, value: unknown): boolean => {
    if (!field.onChangeConfirm || !changeConfirmationIsReady) return false

    requestValueChange(field, value)
    return true
  }

  // Записывает подтверждённое значение и возвращает поле для dispatch.
  const applyPendingFieldChange = (): BaseFieldClass | null => {
    if (!pendingFieldChange.value) return null

    const { field, value } = pendingFieldChange.value
    pendingFieldChange.value = null
    field.value = value

    return field
  }

  // Отменяет изменение и пересоздаёт редактор поля, чтобы его визуальное
  // состояние снова соответствовало неизменённому значению модели.
  const cancelPendingFieldChange = () => {
    if (!pendingFieldChange.value) return

    const { field } = pendingFieldChange.value
    pendingFieldChange.value = null
    fieldRenderVersions.set(field, getEditableFieldKey(field) + 1)
  }

  // Включает подтверждения после завершения начальной настройки формы.
  const enableFieldChangeConfirmation = () => {
    changeConfirmationIsReady = true
  }

  return {
    pendingFieldChange,
    getEditableField,
    getEditableFieldKey,
    requestFieldUpdate,
    applyPendingFieldChange,
    cancelPendingFieldChange,
    enableFieldChangeConfirmation
  }
}
